import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, fmtBRL, fmtCompetencia, fmtDateTime, currentCompetencia, fetchLastCompetencia } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowDown, ArrowUp, Filter, Wallet, Search, X } from "lucide-react";

const FILTROS = [
  { key: "TODOS", label: "Todos" },
  { key: "ADIMPLENTES", label: "Quem Pagou" },
  { key: "INADIMPLENTES", label: "Não Pagou" },
  { key: "PAGO", label: "Somente Pagos" },
  { key: "ABERTO", label: "Somente Abertos" },
];

export default function AdminExtrato() {
  const [filtro, setFiltro] = useState("TODOS");
  const [competencia, setCompetencia] = useState(currentCompetencia());

  useEffect(() => { fetchLastCompetencia().then((c) => c && setCompetencia(c)); }, []);
  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [data, setData] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca), 250);
    return () => clearTimeout(t);
  }, [busca]);

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams({ filtro, competencia });
      if (buscaDebounced.trim()) q.append("busca", buscaDebounced.trim());
      // Só busca lançamentos no filtro "TODOS" — evita chamada desnecessária
      // e evita expor lançamentos irrelevantes quando o admin filtra por status.
      if (filtro === "TODOS") {
        const [e, l] = await Promise.all([
          api.get(`/extrato?${q.toString()}`),
          api.get("/extrato/lancamentos"),
        ]);
        setData(e.data);
        setLancamentos(l.data);
      } else {
        const e = await api.get(`/extrato?${q.toString()}`);
        setData(e.data);
        setLancamentos([]);
      }
    } catch { /* ignore */ }
  }, [filtro, competencia, buscaDebounced]);

  useEffect(() => { load(); }, [load]);

  const lancamentosFiltrados = useMemo(() => {
    if (!buscaDebounced.trim()) return lancamentos;
    const q = buscaDebounced.toLowerCase();
    return lancamentos.filter((l) => (l.descricao || "").toLowerCase().includes(q));
  }, [lancamentos, buscaDebounced]);

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-tag mb-2">Extrato bancário do shopping</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Extrato & Liquidação</h1>
        </div>
        <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-full sm:w-44 rounded-sm" data-testid="extrato-competencia" />
      </div>

      {/* Busca */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          data-testid="extrato-busca"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome do lojista, razão social ou CNPJ..."
          className="pl-10 pr-10 h-11 rounded-sm text-base"
        />
        {busca && (
          <button
            onClick={() => setBusca("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="extrato-limpar-busca"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            data-testid={`filtro-${f.key}`}
            onClick={() => setFiltro(f.key)}
            className={`px-4 py-2 border rounded-sm text-xs font-semibold uppercase tracking-widest transition-colors ${
              filtro === f.key ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div data-testid="card-recebido" className="stat-block p-5 rounded-sm">
            <div className="label-tag">Recebido</div>
            <div className="stat-num text-2xl mt-2 text-success">{fmtBRL(data.resumo.total_recebido)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">boletos com status PAGO</div>
          </div>
          <div data-testid="card-a-vencer" className="stat-block p-5 rounded-sm">
            <div className="label-tag">A vencer</div>
            <div className="stat-num text-2xl mt-2 text-info">{fmtBRL(data.resumo.total_a_vencer ?? 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">em aberto, dentro do prazo</div>
          </div>
          <div data-testid="card-vencido" className="stat-block p-5 rounded-sm">
            <div className="label-tag">Vencido (inadimplência)</div>
            <div className="stat-num text-2xl mt-2 text-destructive">{fmtBRL(data.resumo.total_vencido)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">em aberto, após o vencimento</div>
          </div>
          <div data-testid="card-pendente" className="stat-block p-5 rounded-sm">
            <div className="label-tag">Pendente total</div>
            <div className="stat-num text-2xl mt-2 text-warning">{fmtBRL(data.resumo.total_pendente)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">A vencer + Vencido</div>
          </div>
          <div className={`stat-block p-5 rounded-sm ${data.resumo.saldo_acumulado >= 0 ? "border-success/40" : "border-destructive/40"}`}>
            <div className="label-tag">Saldo acumulado</div>
            <div className={`stat-num text-2xl mt-2 ${data.resumo.saldo_acumulado >= 0 ? "text-success" : "text-destructive"}`}>
              {fmtBRL(data.resumo.saldo_acumulado)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">recebidos − despesas</div>
          </div>
        </div>
      )}

      {/*
        Layout dinâmico:
        - Filtro "TODOS" → grid 2 colunas (Boletos + Lançamentos)
        - Qualquer filtro específico (ADIMPLENTES/INADIMPLENTES/PAGO/ABERTO) →
          Lançamentos ocultos para não poluir a leitura. O bloco "Boletos"
          ocupa toda a largura e reflete estritamente o filtro.
      */}
      <div className={filtro === "TODOS" ? "grid lg:grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
        <Card className="p-6 rounded-sm border-border">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} />
            <h2 className="font-heading font-bold text-xl">
              Boletos ({data?.boletos.length || 0})
              {buscaDebounced && <span className="text-xs text-muted-foreground ml-2 font-normal">para "{buscaDebounced}"</span>}
            </h2>
          </div>
          {!data || data.boletos.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm border border-dashed rounded-sm">
              Nenhum boleto no filtro selecionado.
            </div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {data.boletos.map((b) => (
                <div key={b.id} data-testid={`extrato-b-${b.id}`} className="border border-border p-3 rounded-sm min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm break-anywhere">{b.razao_social}</div>
                      <div className="text-xs text-muted-foreground font-mono break-anywhere">
                        {b.cnpj} · Venc {b.vencimento}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="stat-num text-base">{fmtBRL(b.valor_total)}</div>
                      <Badge variant="outline" className={`text-[9px] uppercase tracking-widest mt-1 ${
                        b.status === "PAGO" ? "border-success/40 text-success bg-success/5" :
                        b.vencido ? "border-destructive/40 text-destructive bg-destructive/5" :
                        "border-warning/40 text-warning bg-warning/5"
                      }`}>
                        {b.status === "PAGO" ? "Pago" : b.vencido ? "Vencido" : "Aberto"}
                      </Badge>
                    </div>
                  </div>
                  {b.pago_em && (
                    <div className="text-[10px] font-mono text-success mt-2 pt-2 border-t border-border/50">
                      Confirmado em: {fmtDateTime(b.pago_em)}
                      {b.baixa_manual_por && <span className="text-muted-foreground"> · Baixa manual por {b.baixa_manual_por}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {filtro === "TODOS" && (
          <Card className="p-6 rounded-sm border-border">
            <div className="flex items-center gap-2 mb-4">
              <Wallet size={16} />
              <h2 className="font-heading font-bold text-xl">Lançamentos ({lancamentosFiltrados.length})</h2>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {lancamentosFiltrados.map((l, i) => (
                <div key={i} data-testid={`extrato-l-${i}`} className="border border-border p-3 rounded-sm flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center ${l.tipo === "ENTRADA" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                    {l.tipo === "ENTRADA" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium break-anywhere">{l.descricao}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {l.data ? fmtDateTime(l.data) : "—"} · {l.origem}
                    </div>
                  </div>
                  <div className={`stat-num text-sm flex-shrink-0 ${l.valor >= 0 ? "text-success" : "text-destructive"}`}>
                    {l.valor >= 0 ? "+" : ""}{fmtBRL(l.valor)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
